import { ITimelineEvent, IMemoryStats, MEMORY_CONSTANTS } from '@/types/timeline';

/**
 * 객체의 메모리 사용량을 추정합니다 (JSON 문자열 길이 기반)
 */
export const estimateMemoryUsage = (obj: any): number => {
  try {
    return JSON.stringify(obj).length * 2; // UTF-16 문자당 2바이트
  } catch (error) {
    // 순환 참조나 직렬화 불가능한 객체의 경우 기본값 반환
    return 1024; // 1KB 기본값
  }
};

/**
 * 큰 객체를 요약 정보로 압축합니다
 */
export const compressLargeObject = (obj: any, maxSize: number = MEMORY_CONSTANTS.MAX_OBJECT_SIZE): any => {
  const originalSize = estimateMemoryUsage(obj);
  
  if (originalSize <= maxSize) {
    return obj;
  }

  // 객체 타입별 압축 전략
  if (Array.isArray(obj)) {
    return {
      __compressed: true,
      __type: 'array',
      __originalSize: originalSize,
      __length: obj.length,
      __sample: obj.slice(0, 3), // 처음 3개 요소만 샘플로 저장
      __summary: `Array with ${obj.length} items (${(originalSize / 1024).toFixed(1)}KB)`
    };
  }

  if (obj && typeof obj === 'object') {
    const keys = Object.keys(obj);
    const sampleKeys = keys.slice(0, 5); // 처음 5개 키만 샘플로 저장
    const sample: any = {};
    
    sampleKeys.forEach(key => {
      const value = obj[key];
      const valueSize = estimateMemoryUsage(value);
      
      if (valueSize > 512) { // 512바이트 이상의 값은 타입 정보만 저장
        sample[key] = `[${typeof value}] ${(valueSize / 1024).toFixed(1)}KB`;
      } else {
        sample[key] = value;
      }
    });

    return {
      __compressed: true,
      __type: 'object',
      __originalSize: originalSize,
      __keyCount: keys.length,
      __sample: sample,
      __allKeys: keys,
      __summary: `Object with ${keys.length} properties (${(originalSize / 1024).toFixed(1)}KB)`
    };
  }

  // 문자열인 경우
  if (typeof obj === 'string' && obj.length > 1000) {
    return {
      __compressed: true,
      __type: 'string',
      __originalSize: originalSize,
      __length: obj.length,
      __preview: obj.substring(0, 200) + '...',
      __summary: `String with ${obj.length} characters (${(originalSize / 1024).toFixed(1)}KB)`
    };
  }

  return obj;
};

/**
 * 이벤트 데이터를 압축합니다
 */
export const compressEventData = (event: ITimelineEvent): ITimelineEvent => {
  const originalSize = estimateMemoryUsage(event.data);
  
  if (originalSize <= MEMORY_CONSTANTS.COMPRESSION_THRESHOLD) {
    return {
      ...event,
      memorySize: originalSize,
      isCompressed: false
    };
  }

  const compressedData = compressLargeObject(event.data);
  const compressedSize = estimateMemoryUsage(compressedData);

  return {
    ...event,
    data: compressedData,
    memorySize: compressedSize,
    originalDataSize: originalSize,
    isCompressed: true
  };
};

/**
 * 이벤트 배열에서 오래된 이벤트를 제거합니다
 */
export const cleanupOldEvents = (
  events: ITimelineEvent[], 
  maxEvents: number = MEMORY_CONSTANTS.MAX_EVENTS
): ITimelineEvent[] => {
  if (events.length <= maxEvents) {
    return events;
  }

  // 북마크된 이벤트는 보존
  const bookmarkedEvents = events.filter(event => event.isBookmarked);
  const regularEvents = events.filter(event => !event.isBookmarked);

  // 최신 이벤트들을 우선 보존 (북마크 제외)
  const eventsToKeep = maxEvents - bookmarkedEvents.length;
  const recentEvents = regularEvents
    .sort((a, b) => b.timestamp - a.timestamp) // 최신순 정렬
    .slice(0, Math.max(0, eventsToKeep));

  return [...bookmarkedEvents, ...recentEvents]
    .sort((a, b) => a.timestamp - b.timestamp); // 시간순으로 재정렬
};

/**
 * 메모리 통계를 계산합니다
 */
export const calculateMemoryStats = (events: ITimelineEvent[]): IMemoryStats => {
  const totalEvents = events.length;
  const totalMemoryUsage = events.reduce((sum, event) => {
    return sum + (event.memorySize || estimateMemoryUsage(event));
  }, 0);
  
  const compressedEvents = events.filter(event => event.isCompressed).length;
  const averageEventSize = totalEvents > 0 ? totalMemoryUsage / totalEvents : 0;

  return {
    totalEvents,
    totalMemoryUsage,
    compressedEvents,
    lastCleanupTime: Date.now(),
    averageEventSize
  };
};

/**
 * 배치 처리를 위해 이벤트들을 그룹화합니다
 */
export const batchEvents = <T>(
  items: T[], 
  batchSize: number = MEMORY_CONSTANTS.BATCH_SIZE
): T[][] => {
  const batches: T[][] = [];
  
  for (let i = 0; i < items.length; i += batchSize) {
    batches.push(items.slice(i, i + batchSize));
  }
  
  return batches;
};

/**
 * 메모리 사용량을 사람이 읽기 쉬운 형태로 포맷합니다
 */
export const formatMemorySize = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes}B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)}KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  }
};

/**
 * 메모리 압박 상황인지 확인합니다
 */
export const isMemoryPressure = (stats: IMemoryStats): boolean => {
  const maxMemory = 50 * 1024 * 1024; // 50MB 제한
  const maxEvents = MEMORY_CONSTANTS.MAX_EVENTS;
  
  return stats.totalMemoryUsage > maxMemory || 
         stats.totalEvents > maxEvents * 0.9; // 90% 임계점
};

/**
 * 이벤트가 중요한지 판단합니다 (정리 시 보존 우선순위)
 */
export const isImportantEvent = (event: ITimelineEvent): boolean => {
  return event.isBookmarked || 
         event.isError || 
         event.severity === 'critical' ||
         event.type === 'error';
};

/**
 * 스마트 이벤트 정리 (중요도 기반)
 */
export const smartCleanup = (events: ITimelineEvent[]): ITimelineEvent[] => {
  if (events.length <= MEMORY_CONSTANTS.CLEANUP_THRESHOLD) {
    return events;
  }

  // 중요한 이벤트들을 먼저 분리
  const importantEvents = events.filter(isImportantEvent);
  const regularEvents = events.filter(event => !isImportantEvent(event));

  // 일반 이벤트들 중에서 최신 것들만 보존
  const eventsToKeep = Math.max(
    100, // 최소 100개는 보존
    MEMORY_CONSTANTS.MAX_EVENTS - importantEvents.length
  );

  const recentRegularEvents = regularEvents
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, eventsToKeep);

  return [...importantEvents, ...recentRegularEvents]
    .sort((a, b) => a.timestamp - b.timestamp);
};
